import { ArrowRight, Cpu } from 'lucide-react';
import { Button } from './ui/button';

export const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToMission = () => {
    const element = document.getElementById('mission');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-24">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1587845323226-bad89242c735?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxtaWNyb2NoaXB8ZW58MHx8fHwxNzc2Mjg2ODU0fDA&ixlib=rb-4.1.0&q=85"
          alt="Microchip background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-[#BB0000]/30 to-black/70"></div>
      </div>

      {/* Animated Circuit Lines */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-10 w-32 h-px bg-white animate-pulse"></div>
        <div className="absolute top-1/2 right-20 w-24 h-px bg-white animate-pulse delay-300"></div>
        <div className="absolute bottom-1/3 left-1/3 w-40 h-px bg-white animate-pulse delay-500"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur-md rounded-full mb-8 animate-fade-in">
          <Cpu className="text-white" size={32} />
        </div>
        
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 animate-fade-in-up leading-tight" style={{ textShadow: '0 4px 12px rgba(0, 0, 0, 0.8)' }}>
          Empowering the Next Generation of
          <span className="block text-[#BB0000] mt-2 font-black" style={{ textShadow: '0 0 20px rgba(0, 0, 0, 0.9), 0 0 40px rgba(0, 0, 0, 0.7), 2px 2px 8px rgba(255, 255, 255, 0.3)' }}>Chip Designers</span>
        </h1>
        
        <p className="text-xl sm:text-2xl text-gray-100 mb-12 max-w-3xl mx-auto animate-fade-in-up animation-delay-200 leading-relaxed" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)' }}>
          Ohio State's premier student organization bridging the gap between 
          undergraduate education and the semiconductor industry
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up animation-delay-400">
          <Button 
            onClick={scrollToContact}
            size="lg" 
            className="bg-[#BB0000] hover:bg-[#990000] text-white px-8 py-6 text-lg font-semibold transition-all hover:scale-105 shadow-lg hover:shadow-xl group"
          >
            Join Buckeye Silicon
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
          
          <Button 
            onClick={scrollToMission}
            size="lg" 
            variant="outline" 
            className="px-8 py-6 text-lg font-semibold bg-white/10 backdrop-blur-md text-white border-white/30 hover:bg-white/20 hover:border-white transition-all"
          >
            Learn More
          </Button>

          <Button 
            onClick={() => window.open('https://discord.gg/VmZyxw5dEV', '_blank')}
            size="lg" 
            className="px-8 py-6 text-lg font-semibold bg-[#5865F2] hover:bg-[#4752C4] text-white transition-all hover:scale-105 shadow-lg hover:shadow-xl group"
          >
            <svg className="mr-2" width="20" height="20" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0)">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z" fill="currentColor"/>
              </g>
            </svg>
            Join Discord
          </Button>
        </div>
      </div>
    </section>
  );
};
